import React, {useEffect, useState} from 'react';
import styles from './EditAdminProjectInfo.module.css';
import FileInput from '../../common/input/FileInputPanel';
import TextCard from '../../common/textCard/TextCard';
import {useNavigate, useParams} from "react-router-dom";
import {ProjectStatus, ProjectType} from "../../../types/project";
import AdminLayout from "../layout/AdminLayout";
import {adminProjectInfoAPI, updateAdminProjectAPI} from "../../../services/admin";
import {UpdateAdminProjectRequest} from "../../../types/admin";
import {UserGroupIcon, XCircleIcon} from "@heroicons/react/24/solid";
import {useAlert} from "../../common/error/AlertContext";
import Button from "../../common/button/Button";

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalUpdateAdminProjectRequest {
    projectId: number;
    projectName: string;
    createdAt: Date;
    startedAt: Date;
    deadline: Date;
    status: ProjectStatus;
    dataAmount: number;
    projectType: ProjectType;
    deleteFile: boolean;
}

//어드민 프로젝트 정보 수정
const EditAdminProjectInfo: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [progress, setProgress] = useState(0);
    const [worker, setWorker] = useState(0);
    const [sampleName, setSampleName] = useState<string | null>(null);
    const {showAlert} = useAlert();

    // 내부적으로는 Date 객체로 관리
    const [formData, setFormData] = useState<InternalUpdateAdminProjectRequest>({
        projectId: 0,
        projectName: '',
        createdAt: new Date(),
        startedAt: new Date(),
        deadline: new Date(),
        status: ProjectStatus.진행중,
        dataAmount: 0,
        projectType: ProjectType.POLYGON,
        deleteFile: false
    });

    // Date 객체를 YYYY-MM-DD 문자열로 변환 (로컬 시간대 기준)
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Date 객체를 화면 표시용 문자열로 변환 (로컬 시간대 기준)
    const formatDateForDisplay = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiFormData = (): UpdateAdminProjectRequest => ({
        projectId: formData.projectId,
        projectName: formData.projectName,
        createdAt: formatDateToString(formData.createdAt),
        startedAt: formatDateToString(formData.startedAt),
        deadline: formatDateToString(formData.deadline),
        status: formData.status,
        dataAmount: formData.dataAmount,
        projectType: formData.projectType,
        deleteFile: formData.deleteFile
    });

    useEffect(() => {
        const fetchProjectRequest = async () => {
            if (!id) return;
            try {
                setLoading(true);
                //어드민 프로젝트 정보 조회 API
                const response = await adminProjectInfoAPI(parseInt(id));
                setFormData({
                    projectId: response.projectId,
                    projectName: response.projectName,
                    createdAt: new Date(response.createdAt),
                    startedAt: new Date(response.startedAt),
                    deadline: new Date(response.deadline),
                    status: response.projectStatus,
                    dataAmount: response.dataAmount,
                    projectType: response.projectType as ProjectType,
                    deleteFile: false
                });
                setProgress(response.progress);
                setWorker(response.workerAmount);
                if (response.fileName) {
                    setSampleName(response.fileName);
                }
            } catch (error) {
                console.error('Error fetching project request:', error);
                navigate('/admin/projects');
            } finally {
                setLoading(false);
            }
        };

        fetchProjectRequest();
    }, [id, navigate]);

    //업로드 가능한 파일 확장자
    const ALLOWED_IMAGE_TYPES = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp'
    ];

    //확장자 검증
    const validateImageFile = (file: File): boolean => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            showAlert('이미지 파일만 업로드 가능합니다.', "error");
            return false;
        }
        return true;
    };

    //업로드할 파일 선택
    const handleFileSelect = (file: File) => {
        if (!validateImageFile(file)) return;
        setUploadedFile(file);
    };

    //업로드 파일 삭제
    const handleDeleteSample = () => {
        setFormData(prev => ({
            ...prev,
            deleteFile: true
        }));
        setSampleName(null);
    };

    //수정
    const handleSubmit = async () => {
        //프로젝트명 빈값 확인
        if (!formData.projectName.trim()) {
            showAlert('프로젝트 이름을 입력해주세요.', "error");
            return;
        }
        try {
            if (id) {
                // Date 객체를 문자열로 변환하여 전송
                const requestData = getApiFormData();

                //프로젝트 업데이트 API
                await updateAdminProjectAPI(requestData, uploadedFile);
                showAlert('수정되었습니다.', "info");
                //프로젝트 상세 페이지로 이동
                navigate(`/admin/projects/${id}`);
            }
        } catch (error) {
            console.error('Error updating project request:', error);
        }
    };

    //프로젝트 날짜 정보
    const projectDates = [
        {
            label: "최초 생성일",
            value: formatDateForDisplay(formData.createdAt),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                createdAt: new Date(value)
            }))
        },
        {
            label: "작업 시작일",
            value: formatDateForDisplay(formData.startedAt),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                startedAt: new Date(value)
            }))
        },
        {
            label: "작업 종료일",
            value: formatDateForDisplay(formData.deadline),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                deadline: new Date(value)
            }))
        },
    ];

    //프로젝트 정보
    const projectDetails = [
        {
            label: "진행 상태",
            value: formData.status,
            type: "select" as const,
            options: Object.values(ProjectStatus),
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                status: value as ProjectStatus
            }))
        },
        {
            label: "데이터 수량",
            value: formData.dataAmount.toLocaleString(),
            type: "text" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                dataAmount: parseInt(value) || 0
            }))
        },
        {
            label: "가공 방식",
            value: formData.projectType,
            type: "select" as const,
            options: Object.values(ProjectType),
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                projectType: value as ProjectType
            }))
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <AdminLayout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1 className={styles.title}>Edit Project</h1>
                        <p className={styles.subtitle}>프로젝트 수정</p>
                    </div>
                    <div className={styles.actions}>
                        {/*저장 버튼*/}
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                        >
                            저장
                        </Button>
                        {/*취소 버튼*/}
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/admin/projects/${id}`)}
                        >
                            취소
                        </Button>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.leftSection}>
                        {/*프로젝트명 입력창*/}
                        <span className={styles.label}>프로젝트 명</span>
                        <input
                            type="text"
                            className={styles.projectNameInput}
                            placeholder="프로젝트명"
                            value={formData.projectName}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                projectName: e.target.value
                            }))}
                        />
                        <div className={styles.progressBar}>
                            {/*마감일 기준 작업 달성률 바 그래프*/}
                            <span className={styles.label}>작업 달성률</span>
                            <svg viewBox="0 0 200 30">
                                <rect
                                    x="0"
                                    y="5"
                                    width="200"
                                    height="20"
                                    rx="2"
                                    fill="#E0E0E0"
                                />
                                <rect
                                    x="0"
                                    y="5"
                                    width={(progress || 0) * 2}
                                    height="20"
                                    rx="2"
                                    fill="#002387"
                                />
                                <text
                                    x={(progress || 0)}
                                    y="18"
                                    textAnchor="middle"
                                    fill="#ffffff"
                                    fontSize="8"
                                    fontFamily="Arial"
                                >
                                    {`${progress}%`}
                                </text>
                            </svg>
                        </div>
                        <div className={styles.workerContainer}>
                            <div className={styles.headerSection}>
                                <div className={styles.textContainer}>
                                    <div className={styles.iconContainer}>
                                        <UserGroupIcon className={styles.iconImage}/>
                                        <h1>작업자 수</h1>
                                    </div>
                                    <p>프로젝트 고용 인원</p>
                                </div>
                            </div>
                            <div className={styles.bottomSection}>
                                {/*프로젝트 할당 인원*/}
                                <div className={styles.countText}>
                                    <span className={styles.countNumber}>{worker}명</span>
                                </div>
                                {/*프로젝트 할당 인원 변경 버튼*/}
                                <button
                                    className={styles.addButton}
                                    disabled={true}
                                >
                                    변경
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.uploadSection}>
                            {/*프로젝트 샘플 데이터 업로드*/}
                            <FileInput
                                onFileSelect={handleFileSelect}
                                accept={ALLOWED_IMAGE_TYPES.join(',')}
                                maxSize={5 * 1024 * 1024}
                            />
                            {sampleName && !formData.deleteFile && (
                                <div className={styles.sampleFile}>
                                    <span className={styles.fileName}>{sampleName}</span>
                                    {/*프로젝트 샘플 데이터 삭제 버튼*/}
                                    <button
                                        className={styles.deleteButton}
                                        onClick={handleDeleteSample}
                                        type="button"
                                    >
                                        <XCircleIcon className={styles.deleteIcon}/>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className={styles.inputGroup}>
                            {/*프로젝트 정보*/}
                            <div className={styles.detailsCard}>
                                <TextCard items={projectDates}/>
                                <TextCard items={projectDetails}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default EditAdminProjectInfo;